// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import HotspotProduct from "@guestsupply/App/Components/HotspotProduct/HotspotProduct";
import Notify from "@guestsupply/App/Components/Notify";
import SlideShowArrow from "@guestsupply/App/Components/SlideShowArrow/SlideShowArrow";
import { TippyContent } from "@guestsupply/App/EditorTemplates/ImageMapPickerField";
import findProductsByErps from "@guestsupply/App/Handlers/FindProductByErp/FindProductsByErps";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { CustomFieldDefinition, ImageMapPickerDefinition } from "@guestsupply/CustomFieldDefinition";
import LazyImage, { LazyImageProps } from "@guestsupply/MobiusOverrides/LazyImage/LazyImage";
import { customXngageWidget } from "@guestsupply/XDL/constants";
import CaretLeft from "@guestsupply/XDL/Icons/CaretLeft";
import CaretRight from "@guestsupply/XDL/Icons/CaretRight";
import ChevronLeft from "@guestsupply/XDL/Icons/ChevronLeft";
import ChevronRight from "@guestsupply/XDL/Icons/ChevronRight";
import Pause from "@guestsupply/XDL/Icons/Pause";
import Play from "@guestsupply/XDL/Icons/Play";
import { color, getSystemColor } from "@guestsupply/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Tippy from "@tippyjs/react";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel, { EmblaCarouselType, EmblaPluginType } from "embla-carousel-react";
import parse from "html-react-parser";
import isEqual from "lodash/isEqual";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

// eslint-disable-next-line spire/fenced-imports
import { inlinePositioning } from "tippy.js";

const enum fields {
    slides = "slides",
    autoScroll = "autoScroll",
    delay = "delay",
}

const enum slideFields {
    slideTitle = "slideTitle",
    slideSubText = "slideSubText",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "buttonVariant",
    mobileImage = "mobileImage",
    mobileImageUrl = "mobileImageUrl",
    imageUrl = "imageUrl",
    coords = "coords",
}

interface Coords {
    xPos: number;
    yPos: number;
    xPercentage: any;
    yPercentage: any;
    productId: string;
    pointColor: string;
    tippyContent: string;
    content: string;
    openOnload: boolean;
}

interface SlideModel {
    fields: {
        slideTitle: string;
        slideSubText: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        imageUrl: string;
        coords: ImageMapPickerDefinition;
        mobileImage: string;
        mobileImageUrl: LinkFieldValue;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.slides]: SlideModel[];
        [fields.autoScroll]: boolean;
        [fields.delay]: number;
    };
    extendedStyles?: HotspotBannerStyles;
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    const erpNumbers = [] as string[];
    props.fields.slides.forEach(slide => {
        slide.fields.coords &&
            (slide.fields.coords as any).forEach((coord: Coords) => {
                coord.tippyContent === TippyContent.productSpot && coord.productId && erpNumbers.push(coord.productId);
            });
    });

    return {
        isTabletScreen: state.xCommon.isTabletScreen,
        isLoading: state.xHotspotProduct.isErpsLoading,
        hotspotProducts: state.xHotspotProduct.productsByErps,
        erpNumbers,
    };
};

const mapDispatchToProps = {
    findProductsByErps,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface HotspotBannerStyles {
    widgetWrapper?: InjectableCss;
    slidesWrapper?: InjectableCss;
    carouselWrapper?: InjectableCss;
    slideImageAndContentWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideTitle?: TypographyProps;
    slideSubTitle?: TypographyProps;
    slideButton?: ButtonPresentationProps;
    slideImageContainer?: InjectableCss;
    image?: LazyImageProps;
    point?: InjectableCss;
    innerPoint?: InjectableCss;
    sliderTextContainer?: InjectableCss;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    tippyPinWrapper?: InjectableCss;
    arrowStyle?: ClickablePresentationProps;
    notify?: TypographyProps;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    pauseIcon?: IconPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
}

export const styles: HotspotBannerStyles = {};

const HotSpotBanner: React.FC<Props> = ({
    fields: { slides, autoScroll, delay },
    id,
    isTabletScreen,
    hotspotProducts,
    findProductsByErps,
    erpNumbers,
    isLoading,
}) => {
    const [selectedProduct, setSelectedProduct] = useState<Coords>({} as Coords);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [pause, setPause] = useState(!autoScroll);
    const [delayTimer, setDelayTimer] = useState(delay * 1000);

    // to show only one item showing onload
    const hasOneItemOpenOnLoad = useRef(false);

    const { isInShell } = useShellContext();
    const [emblaRef, embla] = useEmblaCarousel();
    const buttonLinks = useGetLinks(slides, o => o.fields.buttonLink || {});
    const mobileImageLinks = useGetLinks(slides, o => o.fields?.mobileImageUrl);
    const history = useHistory();
    const dispatch = useDispatch();

    const scrollNext = useCallback(() => {
        if (!embla || pause) {
            return;
        }

        embla.scrollNext();
    }, [embla, pause]);

    useRecursiveTimeout(scrollNext, pause ? 0 : delayTimer);

    const handleDotClick = (slideNumber: number) => {
        if (delayTimer) {
            setDelayTimer(delayTimer + 1);
        }
        embla && embla.scrollTo(slideNumber);
    };

    const handleArrowClick = (direction: "next" | "previous") => {
        // Adding 1 millisecond forces reset of timeout hook
        if (delayTimer) {
            setDelayTimer(delayTimer + 1);
        }
        switch (direction) {
            case "previous":
                embla && embla.scrollPrev();
                break;
            case "next":
                embla && embla.scrollNext();
        }
    };

    const handleCarouselPause = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setPause(prevState => !prevState);
    };

    useEffect(() => {
        if (erpNumbers && erpNumbers.length > 0) {
            findProductsByErps({
                id,
                productNumbers: erpNumbers,
            });
        }
        return () => {
            // clear the store to allow for new data coming form another widget authored in another page
            dispatch({
                type: "HotspotProduct/resetHotspotInfo" as any,
            });
        };
    }, []);

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    useEffect(() => {
        if (!embla || !slides || slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, slides]);

    if (!slides || !slides.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.widgetWrapper}>
            {slides && slides.length > 1 && (
                <StyledWrapper {...styles.prevArrowWrapper}>
                    <Button
                        {...styles.prevArrowButton}
                        onClick={() => handleArrowClick("previous")}
                        aria-label={translate("Previous")}
                    >
                        <ButtonIcon {...styles.iconProps} src={ChevronLeft} />
                    </Button>
                </StyledWrapper>
            )}
            <StyledWrapper ref={emblaRef} {...styles.slidesWrapper}>
                <StyledWrapper {...styles.carouselWrapper}>
                    {slides &&
                        slides.map((slide, index) => {
                            const buttonLink = buttonLinks[index];
                            const mobileImageLink = mobileImageLinks[index];

                            return (
                                <StyledWrapper
                                    {...styles.slideImageAndContentWrapper}
                                    key={`hotspotSlide_${slide.fields.imageUrl}_${index}`}
                                >
                                    <StyledWrapper {...styles.slideContentWrapper}>
                                        {slide.fields.slideTitle && (
                                            <Typography {...styles.slideTitle}>{slide.fields.slideTitle}</Typography>
                                        )}
                                        {slide.fields.slideSubText && (
                                            <Typography {...styles.slideSubTitle}>
                                                {parse(slide.fields.slideSubText, parserOptions)}
                                            </Typography>
                                        )}
                                        {(slide.fields.buttonLabel || slide.fields.buttonLink?.value) && (
                                            <Button
                                                className="slide-button"
                                                {...styles.slideButton}
                                                variant={slide.fields.buttonVariant}
                                                onClick={() => onClick(buttonLink?.url)}
                                                aria-label={
                                                    slide.fields.buttonLabel || buttonLink?.title || buttonLink?.url
                                                }
                                            >
                                                {slide.fields.buttonLabel || buttonLink?.title || buttonLink?.url}
                                            </Button>
                                        )}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.slideImageContainer}>
                                        {!isTabletScreen ? (
                                            <>
                                                <LazyImage
                                                    // XNG-CHANGES: GUEST-2703
                                                    width="667"
                                                    height="500"
                                                    altText={translate("Hotspot Banner Image")}
                                                    src={slide.fields.imageUrl}
                                                    customTransformations={{ default: "GUEST_Product_600x600" }}
                                                    {...styles.image}
                                                />
                                                {!isLoading &&
                                                    slide.fields.coords &&
                                                    (slide.fields.coords as any).length > 0 &&
                                                    (slide.fields.coords as any)
                                                        .filter(
                                                            (coord: Coords) =>
                                                                coord.tippyContent &&
                                                                (coord.tippyContent === TippyContent.richContentSpot ||
                                                                    hotspotProducts.find(
                                                                        (product: ProductModel) =>
                                                                            product.productNumber === coord.productId,
                                                                    )),
                                                        )
                                                        .map((coord: Coords) => {
                                                            const prevHasOneItemOpenOnLoad =
                                                                hasOneItemOpenOnLoad.current;
                                                            if (coord.openOnload) {
                                                                hasOneItemOpenOnLoad.current = true;
                                                            }
                                                            return (
                                                                <StyledWrapper
                                                                    {...styles.tippyPinWrapper}
                                                                    key={`${coord.xPos}_${coord.yPos}`}
                                                                >
                                                                    <Tippy
                                                                        inlinePositioning={true}
                                                                        plugins={[inlinePositioning]}
                                                                        interactive={true}
                                                                        trigger="click"
                                                                        placement="auto"
                                                                        showOnCreate={
                                                                            prevHasOneItemOpenOnLoad
                                                                                ? false
                                                                                : coord.openOnload
                                                                        }
                                                                        onHide={() => setSelectedProduct({} as Coords)}
                                                                        onMount={() => setSelectedProduct(coord)}
                                                                        offset={[0, 8]}
                                                                        content={
                                                                            coord.tippyContent ===
                                                                            TippyContent.productSpot ? (
                                                                                // <HotspotProduct
                                                                                //     productErp={coord.productId}
                                                                                // />
                                                                                <HotspotProduct
                                                                                    product={hotspotProducts.find(
                                                                                        (product: ProductModel) =>
                                                                                            product.productNumber ===
                                                                                            coord.productId,
                                                                                    )}
                                                                                />
                                                                            ) : coord.content ? (
                                                                                <StyledWrapper>
                                                                                    {parse(
                                                                                        coord.content as string,
                                                                                        parserOptions,
                                                                                    )}
                                                                                </StyledWrapper>
                                                                            ) : (
                                                                                <Notify
                                                                                    {...styles.notify}
                                                                                    type="warning"
                                                                                >
                                                                                    {translate(
                                                                                        "No Content is Authored",
                                                                                    )}
                                                                                </Notify>
                                                                            )
                                                                        }
                                                                        popperOptions={{
                                                                            modifiers: [
                                                                                {
                                                                                    name: "flip",
                                                                                    options: {
                                                                                        fallbackPlacements: [
                                                                                            "right",
                                                                                            "top",
                                                                                        ],
                                                                                    },
                                                                                },
                                                                            ],
                                                                        }}
                                                                    >
                                                                        <StyledWrapper
                                                                            key={`${coord.xPos}_${coord.yPos}`}
                                                                            // onClick={() => loadProduct(coord.productId)}
                                                                            {...styles.point}
                                                                            role="button"
                                                                            className={
                                                                                isEqual(selectedProduct, coord)
                                                                                    ? "selected"
                                                                                    : ""
                                                                            }
                                                                            {...({
                                                                                yPercentage: coord.yPercentage,
                                                                                xPercentage: coord.xPercentage,
                                                                                pointColor: coord.pointColor,
                                                                            } as any)}
                                                                        >
                                                                            <VisuallyHidden as="label">
                                                                                {translate("slide point")}
                                                                            </VisuallyHidden>
                                                                            <StyledWrapper
                                                                                {...({
                                                                                    pointColor: coord.pointColor,
                                                                                } as any)}
                                                                                {...styles.innerPoint}
                                                                            ></StyledWrapper>
                                                                        </StyledWrapper>
                                                                    </Tippy>
                                                                </StyledWrapper>
                                                            );
                                                        })}
                                            </>
                                        ) : (
                                            <Clickable
                                                href={mobileImageLink?.url}
                                                aria-label={translate("Hotspot Banner Image")}
                                            >
                                                <LazyImage
                                                    // XNG-CHANGES: GUEST-2703
                                                    width="667"
                                                    height="500"
                                                    altText={translate("Hotspot Banner Image")}
                                                    src={slide.fields.mobileImage || slide.fields.imageUrl}
                                                    customTransformations={{ default: "GUEST_Product_600x600" }}
                                                    {...styles.image}
                                                />
                                            </Clickable>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                </StyledWrapper>
            </StyledWrapper>
            {slides && slides.length > 1 && (
                <StyledWrapper {...styles.dotsContainerWrapper}>
                    <Clickable onClick={handleCarouselPause}>
                        <Icon src={pause ? Play : Pause} {...styles.pauseIcon} />
                    </Clickable>
                    {slides.map((_, i) => {
                        const dotColorStyles =
                            i === selectedIndex
                                ? css`
                                      background: ${getSystemColor("primary")};
                                      &:hover {
                                          background: ${getSystemColor("common.ui8")};
                                      }
                                  `
                                : "";
                        const dotButtonStyles = {
                            css: css`
                                ${styles.dotButton?.css || ""}
                                ${dotColorStyles}
                            `,
                        };

                        return (
                            <Button
                                className="slide-dot-button"
                                // eslint-disable-next-line react/no-array-index-key
                                key={i}
                                {...dotButtonStyles}
                                onClick={() => handleDotClick(i)}
                                aria-label="slide dot button"
                            />
                        );
                    })}
                </StyledWrapper>
            )}
            {slides && slides.length > 1 && (
                <StyledWrapper className="slide-arrow-wrapper" {...styles.nextArrowWrapper}>
                    <Button
                        className="slide-arrow"
                        {...styles.nextArrowButton}
                        onClick={() => handleArrowClick("next")}
                        aria-label={translate("Next")}
                    >
                        <ButtonIcon {...styles.iconProps} src={ChevronRight} />
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const slidesTab = {
    displayName: "Slides",
    sortOrder: 0,
};

const definition: WidgetDefinition<CustomFieldDefinition> = {
    group: customXngageWidget as any,
    fieldDefinitions: [
        {
            name: fields.slides,
            editorTemplate: "ListField",
            tab: slidesTab,
            getDisplay: (item: HasFields) => {
                return item.fields.slideTitle;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    displayName: "Slide Title",
                    name: slideFields.slideTitle,
                    editorTemplate: "TextField",
                    defualtValue: "",
                },
                {
                    displayName: "Slide Sub Text",
                    name: slideFields.slideSubText,
                    editorTemplate: "RichTextField",
                    defualtValue: "",
                },

                {
                    displayName: "Button Label",
                    name: slideFields.buttonLabel,
                    editorTemplate: "TextField",
                    defualtValue: "",
                },
                {
                    name: slideFields.buttonLink,
                    displayName: "Button Link",
                    editorTemplate: "LinkField",
                    defaultValue: { type: "Page", value: "" },
                },
                {
                    name: slideFields.buttonVariant,
                    displayName: "Button Variant",
                    editorTemplate: "DropDownField",
                    // XNG-CHANGES : GUEST-2657 : Removing tertiary option
                    options: [
                        { displayName: "Primary", value: "primary" },
                        { displayName: "Secondary", value: "secondary" },
                    ],
                    hideEmptyOption: true,
                    defaultValue: "primary",
                    fieldType: "Contextual",
                },
                {
                    displayName: "Mobile Image",
                    name: slideFields.mobileImage,
                    editorTemplate: "ImagePickerField",
                },
                {
                    displayName: "Mobile Image Link",
                    name: slideFields.mobileImageUrl,
                    editorTemplate: "LinkField",
                    defaultValue: { type: "Url", value: "" },
                    fieldType: "General",
                },
                {
                    displayName: "Image Path",
                    name: slideFields.imageUrl,
                    defaultValue: "",
                    editorTemplate: "ImagePickerField",
                },
                {
                    name: slideFields.coords,
                    displayName: "Hot Spot Coordinates",
                    editorTemplate: "ImageMapPickerField",
                    imageUrl: (item: HasFields) => item.fields.imageUrl,
                    fieldType: "General",
                    defaultValue: [] as any,
                },
            ] as any,
        },
        {
            name: fields.autoScroll,
            displayName: "Initial Auto Play State",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            tab: slidesTab,
        },
        {
            name: fields.delay,
            displayName: "delay",
            editorTemplate: "IntegerField",
            defaultValue: 10,
            fieldType: "General",
            tooltip: "Time delay for auto play in Seconds",
            tab: slidesTab,
        },
    ],
};

const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: connect(mapStateToProps, mapDispatchToProps)(HotSpotBanner),
    definition,
};

export default widgetModule;
